import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Copy,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  MoreHorizontal,
  ShieldCheck,
  Trash2,
  UserCog,
  UserPlus,
} from 'lucide-react';
import {
  createAdminAccount,
  deleteAdminAccount,
  getAdminAccounts,
  updateAdminAccount,
} from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { ADMIN_SCOPE_ORDER } from '@/lib/auth';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const inputClass =
  'h-10 border-[rgba(6,43,26,0.16)] focus-visible:ring-[3px] focus-visible:ring-[rgba(212,175,55,0.35)] focus-visible:ring-offset-0 focus-visible:border-[#D4AF37]';

const RESERVED_SUPER_ADMIN_EMAIL = 'cecaalee@gmail.com';

const scopeOptions = [
  {
    value: 'academic',
    label: 'Academic',
    description: 'Courses, programs, exams, grades, submissions, and announcements.',
  },
  {
    value: 'student',
    label: 'Student',
    description: 'Student records, admissions, agencies, documents, and student billing.',
  },
  {
    value: 'admin',
    label: 'Admin',
    description: 'Full admin dashboard, reports, accounting, and account management.',
  },
];

const emptyForm = {
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  access_scope: ['academic'],
};

function genPassword() {
  const charset = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%';
  let out = '';
  for (let i = 0; i < 14; i += 1) {
    out += charset[Math.floor(Math.random() * charset.length)];
  }
  return out;
}

function normalizeScopes(scopes) {
  const clean = Array.isArray(scopes) ? scopes.filter(Boolean) : [];
  if (clean.includes('admin')) return [...ADMIN_SCOPE_ORDER];
  return ADMIN_SCOPE_ORDER.filter((scope) => clean.includes(scope));
}

function toggleScope(scopes, scope) {
  const normalized = normalizeScopes(scopes);
  if (normalized.includes(scope)) {
    if (scope === 'admin') return normalized.filter((item) => item !== 'admin');
    return normalized.filter((item) => item !== scope);
  }
  return normalizeScopes([...normalized, scope]);
}

function cleanError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  return fallback;
}

function initials(account) {
  return [account.first_name, account.last_name]
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'AD';
}

function isSuperAdmin(account) {
  return account?.is_super_admin === true;
}

function isReservedSuperAdminEmail(email) {
  return String(email || '').trim().toLowerCase() === RESERVED_SUPER_ADMIN_EMAIL;
}

function canEditAccount(profile, account) {
  return !isSuperAdmin(account) || profile?.is_super_admin === true;
}

function canDeleteAccount(profile, account) {
  return profile?.is_super_admin === true && !isSuperAdmin(account) && account.id !== profile?.id;
}

function ScopeBadges({ scopes }) {
  const normalized = normalizeScopes(scopes);
  return (
    <div className="flex flex-wrap gap-1.5">
      {scopeOptions.map((scope) => (
        normalized.includes(scope.value) ? (
          <Badge
            key={scope.value}
            variant="outline"
            className="border-[#D4AF37]/35 bg-[#D4AF37]/10 text-[#8A6508]"
          >
            {scope.label}
          </Badge>
        ) : null
      ))}
    </div>
  );
}

function ScopePicker({ value, onChange, disabled = false }) {
  const normalized = normalizeScopes(value);
  return (
    <div className="grid gap-3">
      {scopeOptions.map((scope) => {
        const checked = normalized.includes(scope.value);
        return (
          <label
            key={scope.value}
            className={`flex items-start gap-3 rounded-lg border border-[rgba(6,43,26,0.10)] p-3 transition-colors ${disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:bg-[#F7F8FA]'}`}
          >
            <Checkbox
              checked={checked}
              disabled={disabled}
              onCheckedChange={() => onChange(toggleScope(normalized, scope.value))}
              className="mt-0.5 border-[rgba(6,43,26,0.35)] data-[state=checked]:border-[#062B1A] data-[state=checked]:bg-[#062B1A]"
            />
            <span className="space-y-0.5">
              <span className="block text-sm font-semibold text-[#062B1A]">{scope.label}</span>
              <span className="block text-xs leading-relaxed text-[#062B1A]/55">{scope.description}</span>
            </span>
          </label>
        );
      })}
    </div>
  );
}

export default function AdminAccounts() {
  const { profile } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ ...emptyForm, password: genPassword() });
  const [showPassword, setShowPassword] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingId, setDeletingId] = useState('');

  const sortedAccounts = useMemo(
    () => [...accounts].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))),
    [accounts],
  );
  const hasSuperAdmin = accounts.some(isSuperAdmin);
  const formIsReservedSuperAdmin = isReservedSuperAdminEmail(form.email);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const { data } = await getAdminAccounts();
      setAccounts(data || []);
    } catch (err) {
      toast.error(cleanError(err, 'Could not load accounts'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccounts();
  }, []);

  const updateForm = (key) => (event) => {
    setForm((current) => ({ ...current, [key]: event.target.value }));
  };

  const resetForm = () => {
    setForm({ ...emptyForm, password: genPassword() });
    setShowPassword(true);
  };

  const fillSuperAdminForm = () => {
    setForm((current) => ({
      ...current,
      first_name: current.first_name || 'Super',
      last_name: current.last_name || 'Admin',
      email: RESERVED_SUPER_ADMIN_EMAIL,
      password: current.password || genPassword(),
      access_scope: [...ADMIN_SCOPE_ORDER],
    }));
    setShowPassword(true);
  };

  const validateForm = (target) => {
    if (!target.first_name.trim()) return 'First name is required';
    if (!target.last_name.trim()) return 'Last name is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(target.email.trim())) return 'Enter a valid email';
    if (target.password !== undefined && target.password && target.password.length < 8) return 'Password must be at least 8 characters';
    if (normalizeScopes(target.access_scope).length === 0) return 'Select at least one access area';
    return null;
  };

  const submitCreate = async (event) => {
    event.preventDefault();
    const error = validateForm(form);
    if (error) {
      toast.error(error);
      return;
    }
    if (form.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    try {
      setCreating(true);
      const accessScope = formIsReservedSuperAdmin ? [...ADMIN_SCOPE_ORDER] : normalizeScopes(form.access_scope);
      const { data } = await createAdminAccount({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        access_scope: accessScope,
      });
      setAccounts((current) => [data.account, ...current]);
      resetForm();
      toast.success('Account created');
    } catch (err) {
      toast.error(cleanError(err, 'Could not create account'));
    } finally {
      setCreating(false);
    }
  };

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(form.password);
      toast.success('Password copied');
    } catch {
      toast.error('Could not copy password');
    }
  };

  const openEdit = (account) => {
    if (!canEditAccount(profile, account)) {
      toast.error('Only the super admin can edit the super admin account');
      return;
    }
    setEditing(account);
    setEditForm({
      first_name: account.first_name || '',
      last_name: account.last_name || '',
      email: account.email || '',
      password: '',
      access_scope: normalizeScopes(account.access_scope),
    });
  };

  const updateEditForm = (key) => (event) => {
    setEditForm((current) => ({ ...current, [key]: event.target.value }));
  };

  const submitEdit = async () => {
    const error = validateForm(editForm);
    if (error) {
      toast.error(error);
      return;
    }
    try {
      setSavingEdit(true);
      const payload = {
        first_name: editForm.first_name.trim(),
        last_name: editForm.last_name.trim(),
        email: editForm.email.trim().toLowerCase(),
        access_scope: normalizeScopes(editForm.access_scope),
      };
      if (editForm.password.trim()) payload.password = editForm.password;
      const { data } = await updateAdminAccount(editing.id, payload);
      setAccounts((current) => current.map((account) => (account.id === editing.id ? data.account : account)));
      setEditing(null);
      toast.success('Account updated');
    } catch (err) {
      toast.error(cleanError(err, 'Could not update account'));
    } finally {
      setSavingEdit(false);
    }
  };

  const removeAccount = async (account) => {
    if (!canDeleteAccount(profile, account)) {
      toast.error(isSuperAdmin(account) ? 'The super admin account cannot be deleted' : 'Only the super admin can delete admin accounts');
      return;
    }
    try {
      setDeletingId(account.id);
      await deleteAdminAccount(account.id);
      setAccounts((current) => current.filter((item) => item.id !== account.id));
      toast.success('Account deleted');
    } catch (err) {
      toast.error(cleanError(err, 'Could not delete account'));
    } finally {
      setDeletingId('');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-accounts-page">
      <div>
        <h2
          className="text-2xl font-semibold text-[#062B1A]"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          Accounts
        </h2>
        <p className="mt-1 text-sm text-[#062B1A]/60">
          Create staff accounts and control access. Only the super admin can delete admin accounts.
        </p>
      </div>

      {!loading && !hasSuperAdmin && (
        <Card className="border border-[#D4AF37]/25 bg-[#D4AF37]/10 shadow-sm">
          <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg bg-[#062B1A] text-white">
                <ShieldCheck className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm font-semibold text-[#062B1A]">Super admin is not set up yet</p>
                <p className="mt-1 text-xs leading-relaxed text-[#062B1A]/65">
                  Create the reserved account with {RESERVED_SUPER_ADMIN_EMAIL}; it will receive full access and cannot be deleted.
                </p>
              </div>
            </div>
            <Button
              type="button"
              onClick={fillSuperAdminForm}
              className="h-9 shrink-0 bg-[#062B1A] text-white hover:bg-[#041B10]"
            >
              <UserPlus className="mr-2 h-4 w-4" />
              Fill super admin
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,420px)_1fr]">
        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardHeader className="border-b border-[rgba(6,43,26,0.08)]">
            <CardTitle
              className="flex items-center gap-2 text-base font-semibold text-[#062B1A]"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              <UserPlus className="h-4 w-4 text-[#D4AF37]" />
              New account
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={submitCreate} className="space-y-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="first-name" className="text-sm font-medium text-[#062B1A]">
                    First name
                  </Label>
                  <Input id="first-name" value={form.first_name} onChange={updateForm('first_name')} className={inputClass} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last-name" className="text-sm font-medium text-[#062B1A]">
                    Last name
                  </Label>
                  <Input id="last-name" value={form.last_name} onChange={updateForm('last_name')} className={inputClass} />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="account-email" className="text-sm font-medium text-[#062B1A]">
                  Email
                </Label>
                <Input id="account-email" type="email" value={form.email} onChange={updateForm('email')} className={inputClass} />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="account-password" className="text-sm font-medium text-[#062B1A]">
                    Password
                  </Label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setForm((current) => ({ ...current, password: genPassword() }))}
                      className="text-xs font-medium text-[#062B1A]/60 hover:text-[#D4AF37]"
                    >
                      Regenerate
                    </button>
                    <button
                      type="button"
                      onClick={copyPassword}
                      className="text-xs font-medium text-[#062B1A]/60 hover:text-[#D4AF37]"
                    >
                      <Copy className="mr-1 inline h-3 w-3" />
                      Copy
                    </button>
                  </div>
                </div>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#062B1A]/35" />
                  <Input
                    id="account-password"
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={updateForm('password')}
                    className={`${inputClass} pl-9 pr-10 font-mono`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((current) => !current)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#062B1A]/45 hover:text-[#062B1A]"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {formIsReservedSuperAdmin && (
                <div className="rounded-lg border border-[#D4AF37]/25 bg-[#D4AF37]/10 px-3 py-2 text-sm text-[#8A6508]">
                  This reserved email will be created as Super Admin with full access.
                </div>
              )}
              <ScopePicker
                value={formIsReservedSuperAdmin ? ADMIN_SCOPE_ORDER : form.access_scope}
                onChange={(access_scope) => setForm((current) => ({ ...current, access_scope }))}
                disabled={formIsReservedSuperAdmin}
              />

              <Button
                type="submit"
                disabled={creating}
                className="h-10 bg-[#062B1A] text-white hover:bg-[#041B10]"
              >
                {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />}
                {formIsReservedSuperAdmin ? 'Create super admin' : 'Create account'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardHeader className="border-b border-[rgba(6,43,26,0.08)]">
            <CardTitle
              className="flex items-center gap-2 text-base font-semibold text-[#062B1A]"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              <UserCog className="h-4 w-4 text-[#D4AF37]" />
              Staff accounts
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="space-y-3 p-5">
                {[1, 2, 3].map((item) => (
                  <div key={item} className="h-16 animate-pulse rounded-lg bg-[#F7F8FA]" />
                ))}
              </div>
            ) : sortedAccounts.length === 0 ? (
              <div className="p-8 text-sm text-[#062B1A]/55">No admin accounts found.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-left">
                  <thead className="border-b border-[rgba(6,43,26,0.08)] bg-[#F7F8FA]">
                    <tr className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/50">
                      <th className="px-5 py-3">Account</th>
                      <th className="px-5 py-3">Access</th>
                      <th className="px-5 py-3">Updated</th>
                      <th className="px-5 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgba(6,43,26,0.08)]">
                    {sortedAccounts.map((account) => {
                      const isSelf = account.id === profile?.id;
                      const superAdmin = isSuperAdmin(account);
                      const editAllowed = canEditAccount(profile, account);
                      const deleteAllowed = canDeleteAccount(profile, account);
                      return (
                        <tr key={account.id} className="align-middle">
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-3">
                              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#062B1A] text-sm font-semibold text-white">
                                {initials(account)}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-semibold text-[#062B1A]">{account.name || account.email}</p>
                                  {isSelf && (
                                    <Badge variant="outline" className="border-[#062B1A]/15 bg-[#062B1A]/5 text-[10px] text-[#062B1A]/70">
                                      You
                                    </Badge>
                                  )}
                                  {superAdmin && (
                                    <Badge variant="outline" className="border-[#D4AF37]/45 bg-[#D4AF37]/10 text-[10px] text-[#8A6508]">
                                      Super Admin
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-xs text-[#062B1A]/55">{account.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-4">
                            <ScopeBadges scopes={account.access_scope} />
                          </td>
                          <td className="px-5 py-4 text-sm text-[#062B1A]/55">
                            {account.updated_at ? new Date(account.updated_at).toLocaleDateString() : 'Not set'}
                          </td>
                          <td className="px-5 py-4 text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-9 w-9 text-[#062B1A]/65">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  disabled={!editAllowed}
                                  onClick={() => openEdit(account)}
                                  className="cursor-pointer"
                                >
                                  <ShieldCheck className="mr-2 h-4 w-4" />
                                  Edit access
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  disabled={!deleteAllowed || deletingId === account.id}
                                  onClick={() => removeAccount(account)}
                                  className="cursor-pointer text-red-600 focus:text-red-600"
                                >
                                  {deletingId === account.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
              Edit account
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="edit-first-name" className="text-sm font-medium text-[#062B1A]">
                  First name
                </Label>
                <Input id="edit-first-name" value={editForm.first_name} onChange={updateEditForm('first_name')} className={inputClass} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-last-name" className="text-sm font-medium text-[#062B1A]">
                  Last name
                </Label>
                <Input id="edit-last-name" value={editForm.last_name} onChange={updateEditForm('last_name')} className={inputClass} />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="edit-email" className="text-sm font-medium text-[#062B1A]">
                  Email
                </Label>
                <Input id="edit-email" type="email" value={editForm.email} onChange={updateEditForm('email')} className={inputClass} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-password" className="text-sm font-medium text-[#062B1A]">
                  New password
                </Label>
                <Input
                  id="edit-password"
                  type="password"
                  value={editForm.password}
                  onChange={updateEditForm('password')}
                  placeholder="Leave unchanged"
                  className={inputClass}
                />
              </div>
            </div>
            {isSuperAdmin(editing) && (
              <div className="rounded-lg border border-[#D4AF37]/25 bg-[#D4AF37]/10 px-3 py-2 text-sm text-[#8A6508]">
                Super admin keeps full access and cannot be downgraded.
              </div>
            )}
            <ScopePicker
              value={editForm.access_scope}
              onChange={(access_scope) => setEditForm((current) => ({ ...current, access_scope }))}
              disabled={isSuperAdmin(editing)}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setEditing(null)}
              className="border-[rgba(6,43,26,0.16)] text-[#062B1A]"
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={savingEdit}
              onClick={submitEdit}
              className="bg-[#062B1A] text-white hover:bg-[#041B10]"
            >
              {savingEdit ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
